import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../../contexts/AuthContext';

const OTPVerification = () => {
  const [otp, setOtp]         = useState(['', '', '', '']); // 4-digit OTP
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');
  const [countdown, setCountdown] = useState(60);

  const inputsRef = useRef([]);
  const navigate  = useNavigate();
  const location  = useLocation();
  const { verifyPhone, resendOTP } = useAuth();

  // phone_number passed via navigation state from SignUp
  const phone_number = location.state?.phone_number || '';

  // Redirect if no phone number
  useEffect(() => {
    if (!phone_number) navigate('/signup');
  }, [phone_number, navigate]);

  // Countdown timer for resend
  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  // Auto-focus first input
  useEffect(() => {
    inputsRef.current[0]?.focus();
  }, []);

  const handleChange = (index, value) => {
    if (!/^\d?$/.test(value)) return; // digits only
    const updated = [...otp];
    updated[index] = value;
    setOtp(updated);
    if (value && index < 3) inputsRef.current[index + 1]?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
    if (pasted.length === 4) {
      setOtp(pasted.split(''));
      inputsRef.current[3]?.focus();
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    const code = otp.join('');
    if (code.length < 4) { setError('Please enter the 4-digit OTP.'); return; }

    setLoading(true);
    setError('');

    const result = await verifyPhone(phone_number, code);

    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.message);
      setOtp(['', '', '', '']);
      inputsRef.current[0]?.focus();
    }
    setLoading(false);
  };

  const handleResend = async () => {
    setResending(true);
    setError('');
    setSuccess('');

    const result = await resendOTP(phone_number);

    if (result.success) {
      setSuccess('OTP resent successfully.');
      setCountdown(60);
    } else {
      setError(result.message);
    }
    setResending(false);
  };

  return (
    <div className="otp-container">
      <div className="card">
        <h2>Verify Your Phone</h2>
        <p className="subtitle">
          Enter the 4-digit code sent to <strong>{phone_number}</strong>
        </p>

        {error   && <div className="error-box">{error}</div>}
        {success && <div className="success-box">{success}</div>}

        <form onSubmit={handleVerify}>
          <div className="otp-inputs" onPaste={handlePaste}>
            {otp.map((digit, i) => (
              <input
                key={i}
                ref={el => inputsRef.current[i] = el}
                type="tel"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={e => handleChange(i, e.target.value)}
                onKeyDown={e => handleKeyDown(i, e)}
                className="otp-input"
              />
            ))}
          </div>

          <button type="submit" disabled={loading} className="primary-btn">
            {loading ? 'Verifying...' : 'Verify'}
          </button>
        </form>

        <div className="resend-row">
          {countdown > 0 ? (
            <span className="timer">Resend OTP in {countdown}s</span>
          ) : (
            <button onClick={handleResend} disabled={resending} className="resend-btn">
              {resending ? 'Sending...' : 'Resend OTP'}
            </button>
          )}
        </div>
      </div>

      <style>{`
        .otp-container {
          min-height: 100vh; display: flex; align-items: center; justify-content: center;
          background: radial-gradient(at center, #49AFAF, #A8DFF4);
          font-family: Arial, sans-serif; padding: 20px;
        }
        .card {
          background: white; border-radius: 12px; padding: 40px 32px;
          max-width: 400px; width: 100%; text-align: center;
          box-shadow: 0 4px 24px rgba(0,0,0,0.1);
        }
        h2 { font-size: 24px; font-weight: bold; color: #111827; margin-bottom: 8px; }
        .subtitle { color: #6b7280; font-size: 14px; margin-bottom: 24px; line-height: 1.5; }
        .error-box {
          background: #fee2e2; border: 1px solid #f87171; color: #b91c1c;
          padding: 10px 14px; border-radius: 6px; margin-bottom: 16px; font-size: 14px;
        }
        .success-box {
          background: #d1fae5; border: 1px solid #6ee7b7; color: #065f46;
          padding: 10px 14px; border-radius: 6px; margin-bottom: 16px; font-size: 14px;
        }
        .otp-inputs { display: flex; justify-content: center; gap: 12px; margin-bottom: 24px; }
        .otp-input {
          width: 56px; height: 64px; text-align: center; font-size: 28px; font-weight: bold;
          border: 2px solid #d1d5db; border-radius: 8px; background: #f9fafb;
          color: #111827; outline: none; transition: border-color 0.2s;
        }
        .otp-input:focus { border-color: #3b82f6; background: white; }
        .primary-btn {
          width: 100%; padding: 13px; background: #3b82f6; color: white;
          border: none; border-radius: 50px; font-size: 16px; font-weight: 500;
          cursor: pointer; transition: background 0.2s;
        }
        .primary-btn:hover { background: #2563eb; }
        .primary-btn:disabled { background: #9ca3af; cursor: not-allowed; }
        .resend-row { margin-top: 20px; font-size: 14px; }
        .timer { color: #6b7280; }
        .resend-btn {
          background: none; border: none; color: #3b82f6;
          cursor: pointer; font-size: 14px; font-weight: 500;
          text-decoration: underline; padding: 0;
        }
        .resend-btn:disabled { color: #9ca3af; cursor: not-allowed; }
      `}</style>
    </div>
  );
};

export default OTPVerification;
